import { useState } from "react";
import { useFormSubmit } from "@/hooks/useFormSubmit";

const FORM_ADDR = "https://readdy.ai/api/form/da2m29rd3r93mn1k3bsg";

const inputClass =
  "w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition";

function getImpact(amount: number): string {
  if (amount >= 250) return "≈ 1 kit scolaire complet pour 5 enfants";
  if (amount >= 100) return "≈ 3 paniers alimentaires pour des familles";
  if (amount >= 50) return "≈ 1 panier alimentaire pour une famille";
  if (amount >= 25) return "≈ 12 repas chauds pour des personnes démunies";
  if (amount >= 10) return "≈ 5 repas chauds pour des personnes démunies";
  return "Chaque euro compte pour soutenir nos actions";
}

export default function DonationForm({
  amounts = [],
}: {
  amounts?: number[];
}) {
  const [frequency, setFrequency] = useState<"unique" | "mensuel">("unique");
  const [selectedAmount, setSelectedAmount] = useState<number>(
    amounts[0] ?? 25
  );
  const [customValue, setCustomValue] = useState("");

  const { status, message, handleSubmit } = useFormSubmit(FORM_ADDR);

  const effectiveAmount =
    customValue.trim() !== "" && Number(customValue) > 0
      ? Number(customValue)
      : selectedAmount;

  const formatAmount = (n: number) =>
    n % 1 === 0 ? String(n) : n.toFixed(2);

  return (
    <div className="bg-background-50 rounded-lg border border-background-200 p-6 md:p-8">
      <h3 className="font-heading text-xl font-semibold text-foreground-950">
        Votre don
      </h3>
      <p className="mt-1.5 text-sm text-foreground-600">
        Choisissez un montant et une fréquence, chaque geste compte.
      </p>

      <form
        id="form-don"
        data-readdy-form
        onSubmit={handleSubmit}
        className="mt-6"
      >
        <input
          type="text"
          name="website_alt"
          className="field-offscreen"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />

        <div>
          <span className="block text-sm font-medium text-foreground-700 mb-2">
            Fréquence
          </span>
          <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
            <button
              type="button"
              onClick={() => setFrequency("unique")}
              className={`flex-1 px-4 sm:px-6 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                frequency === "unique"
                  ? "bg-primary-500 text-background-50"
                  : "text-foreground-600 hover:text-foreground-900"
              }`}
            >
              Don unique
            </button>
            <button
              type="button"
              onClick={() => setFrequency("mensuel")}
              className={`flex-1 px-4 sm:px-6 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                frequency === "mensuel"
                  ? "bg-primary-500 text-background-50"
                  : "text-foreground-600 hover:text-foreground-900"
              }`}
            >
              Don mensuel
            </button>
          </div>
        </div>

        <div className="mt-6">
          <span className="block text-sm font-medium text-foreground-700 mb-3">
            Montant
          </span>
          <div className="flex flex-wrap gap-3">
            {amounts.map((amount) => {
              const active =
                customValue.trim() === "" && selectedAmount === amount;
              return (
                <button
                  key={amount}
                  type="button"
                  onClick={() => {
                    setSelectedAmount(amount);
                    setCustomValue("");
                  }}
                  className={`px-6 py-3 rounded-full border text-base font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                    active
                      ? "bg-primary-500 text-background-50 border-primary-500"
                      : "bg-background-50 text-foreground-800 border-background-300 hover:border-primary-400"
                  }`}
                >
                  {amount} €
                </button>
              );
            })}

            <div className="flex items-center gap-1 rounded-full border border-background-300 bg-background-50 px-4 py-1.5 focus-within:ring-2 focus-within:ring-primary-400">
              <input
                type="number"
                min="1"
                value={customValue}
                onChange={(e) => setCustomValue(e.target.value)}
                placeholder="Autre"
                className="w-20 bg-transparent text-base font-semibold text-foreground-900 placeholder:text-foreground-400 focus:outline-none text-center"
                aria-label="Montant libre"
              />
              <span className="text-base font-semibold text-foreground-800">
                €
              </span>
            </div>
          </div>
        </div>

        <p className="mt-5 flex items-start gap-2 rounded-md bg-primary-100 px-4 py-3 text-sm font-medium text-primary-700">
          <i className="ri-heart-3-fill text-primary-600 mt-0.5"></i>
          {getImpact(effectiveAmount)}
          {frequency === "mensuel" && " chaque mois"}
        </p>

        <input type="hidden" name="montant" value={formatAmount(effectiveAmount)} />
        <input type="hidden" name="frequence" value={frequency} />

        <div className="mt-6 grid grid-cols-1 gap-5">
          <div>
            <label
              htmlFor="don-nom"
              className="block text-sm font-medium text-foreground-700 mb-1.5"
            >
              Nom complet
            </label>
            <input
              id="don-nom"
              name="nom"
              type="text"
              required
              placeholder="Votre nom"
              className={inputClass}
            />
          </div>
          <div>
            <label
              htmlFor="don-email"
              className="block text-sm font-medium text-foreground-700 mb-1.5"
            >
              Email
            </label>
            <input
              id="don-email"
              name="email"
              type="email"
              required
              placeholder="vous@exemple.fr"
              className={inputClass}
            />
          </div>
        </div>

        {status === "success" && (
          <div className="mt-5 rounded-md bg-primary-100 text-primary-700 px-4 py-3 text-sm font-medium">
            <i className="ri-checkbox-circle-line mr-1.5"></i>
            {message}
          </div>
        )}
        {status === "error" && (
          <div className="mt-5 rounded-md bg-accent-100 text-accent-700 px-4 py-3 text-sm font-medium">
            <i className="ri-error-warning-line mr-1.5"></i>
            {message}
          </div>
        )}

        <button
          type="submit"
          disabled={status === "loading"}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 px-8 py-4 text-base font-semibold transition-all duration-200 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {status === "loading" ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i>
              Traitement en cours…
            </>
          ) : (
            <>
              <i className="ri-heart-3-fill"></i>
              Je donne {formatAmount(effectiveAmount)} €
              {frequency === "mensuel" ? " / mois" : ""}
            </>
          )}
        </button>

        <p className="mt-4 text-center text-xs text-foreground-500">
          <i className="ri-lock-line mr-1"></i>
          Paiement sécurisé · Don déductible à 66% de vos impôts
        </p>
      </form>
    </div>
  );
}